import PropTypes from 'prop-types';
// @mui
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Rating from '@mui/material/Rating';
import Avatar from '@mui/material/Avatar';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
// utils
import { fCurrency, fShortenNumber } from 'src/utils/format-number';
// components
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { Tab, Tabs } from '@mui/material';
import { useCallback, useState } from 'react';

// ----------------------------------------------------------------------

export default function AppTopRelated({ title, subheader, list, ...other }) {
  const [ currentTab, setCurrentTab ] = useState('one');

  const handleChangeTab = useCallback((event, newValue) => {
    setCurrentTab(newValue);
  }, []);

  const TABS = [
    {
      value: 'one',
      icon: <Iconify icon="material-symbols:all-inbox" width={ 24 } />,
      label: 'Inbox',
    },
    {
      value: 'two',
      icon: <Iconify icon="material-symbols:browse-activity-rounded" width={ 24 } />,
      label: 'Activities',
    },
    {
      value: 'three',
      icon: <Iconify icon="material-symbols:supervised-user-circle" width={ 24 } />,
      label: 'Followers',
    },
  ];

  // Example filtered data by tab
  const filteredList = list.filter((app) => {
    if (currentTab === 'one') return app.category === 'inbox';
    if (currentTab === 'two') return app.category === 'activity';
    if (currentTab === 'three') return app.category === 'followers';
    return true;
  });

  return (
    <Card { ...other }>
      <CardHeader title={ title } subheader={ subheader } />
      <Scrollbar>
        <Tabs
          value={ currentTab }
          onChange={ handleChangeTab }
          variant="fullWidth"
          textColor="primary"
          indicatorColor="primary"
        >
          { TABS.map((tab) => (
            <Tab
              key={ tab.value }
              icon={ tab.icon }
              label={ tab.label }
              value={ tab.value }
              disabled={ tab.disabled }
            />
          )) }
        </Tabs>

        <Stack spacing={ 3 } sx={ { p: 3, minWidth: 360 } }>
          { list.map((app) => (
            <ApplicationItem key={ app.id } app={ app } />
          )) }
        </Stack>
      </Scrollbar>
    </Card>
  );
}


AppTopRelated.propTypes = {
  list: PropTypes.array,
  subheader: PropTypes.string,
  title: PropTypes.string,
};

// ----------------------------------------------------------------------

function ApplicationItem({ app }) {
  const { shortcut, system, price, ratingNumber, totalReviews, name } = app;

  return (
    <Stack direction="row" alignItems="center" spacing={ 2 }>
      <Avatar
        variant="rounded"
        sx={ {
          width: 48,
          height: 48,
          bgcolor: 'background.neutral',
        } }
      >
        <Box component="img" src={ shortcut } sx={ { width: 24, height: 24 } } />
      </Avatar>

      <Box sx={ { flexGrow: 1, minWidth: 0 } }>
        <Typography variant="subtitle2" noWrap>
          { name }
        </Typography>

        <Stack direction="row" alignItems="center" sx={ { mt: 0.5, color: 'text.secondary' } }>
          <Iconify
            width={ 14 }
            icon={ system === 'Mac' ? 'mingcute:apple-fill' : 'mingcute:windows-fill' }
          />

          <Typography variant="caption" sx={ { ml: 0.5, mr: 1 } }>
            { system }
          </Typography>

          <Label color={ price === 0 ? 'success' : 'error' }>
            { price === 0 ? 'Free' : fCurrency(price) }
          </Label>
        </Stack>
      </Box>

      <Stack alignItems="flex-end">
        <Rating readOnly size="small" precision={ 0.5 } name="reviews" value={ ratingNumber } />
        <Typography variant="caption" sx={ { mt: 0.5, color: 'text.secondary' } }>
          { fShortenNumber(totalReviews) } reviews
        </Typography>
      </Stack>
    </Stack>
  );
}

ApplicationItem.propTypes = {
  app: PropTypes.object,
};
